import { Feature, MultiPolygon, Point, Polygon, GeoJsonGeometryTypes, FeatureCollection } from "geojson";
import type { BaseDataModel } from "../api/types";
import { PlusOne, SvgIconComponent, Upload, UploadFile } from "@mui/icons-material";
import React from "react";

//#region Job

export const JobsPriorities = [
	"Urgent",
	"Regular Turn (default)",
	"No Rush (turn+1wk)",
	"Flexible (before freezing)",
	"Winter Sample Acceptable",
	"Flexible (before growth stage)",
	"Flexible (before planting)",
	"In-crop Acceptable ",
	"Flexible (within 2 weeks)",
] as const;

export type JobsPriority = (typeof JobsPriorities)[number];

/** Get appropriate job priorities for the season */
export function getJobPriorities(): JobsPriority[] {
	const now = new Date();
	const month = now.getMonth() + 1; // getMonth() returns 0-11, so add 1

	// Always available priorities
	const basePriorities: JobsPriority[] = ["Urgent", "Regular Turn (default)", "No Rush (turn+1wk)"];

	// Spring priorities (January 1 - July 31)
	const springPriorities: JobsPriority[] = [
		"Flexible (before growth stage)",
		"Flexible (before planting)",
		"In-crop Acceptable ",
	];

	// Fall priorities (August 1 - December 31)
	const fallPriorities: JobsPriority[] = ["Flexible (before freezing)", "Winter Sample Acceptable"];

	if (month >= 1 && month <= 7) {
		// Spring time - show base + spring priorities
		return [...basePriorities, ...springPriorities];
	} else {
		// Fall time - show base + fall priorities
		return [...basePriorities, ...fallPriorities];
	}
}

export const JobFlags = [
	"Bulk Upload",
	"Bulk Upload - Completed",
	"Created Portal Single",
	"Created Portal Bulk",
	"Created Portal API Proagrica",
	"Created Portal API Agrian",
	"Agrian",
	"ROGO Created",
] as const;

export type JobFlags = (typeof JobFlags)[number];

export const BoundaryChangeTypes = ["Major", "Minor"] as const;

export type BoundaryChangeType = (typeof BoundaryChangeTypes)[number];

//#endregion

//#region Settings

export enum ViewGroup {
	Default = "Default",
	Company = "Company Views",
	User = "User Views",
}

export interface ViewDefinition {
	name: string;
	group: ViewGroup;
	url: string;
	default?: boolean;
	lastUpdated?: string;
}

interface PortalSettings {
	views: ViewDefinition[];
}

// oxlint-disable-next-line no-empty-object-type
export interface UserSettings extends PortalSettings {}

interface ImportTableColumnSetting {
	visible: boolean;
	required: boolean;
}

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// Maintain the same order in all places for consistency.
// ==========================================================================================
export interface CompanySettings extends PortalSettings {
	importTableColumns: {
		// Basic Information
		grower: ImportTableColumnSetting;
		farm: ImportTableColumnSetting;
		field: ImportTableColumnSetting;

		// Attachments
		boundaryGeoJSON: ImportTableColumnSetting;
		pointsGeoJSON: ImportTableColumnSetting;
		zonesGeoJSON: ImportTableColumnSetting;
		fieldNotes: ImportTableColumnSetting;

		// Order
		isReadyToSample: ImportTableColumnSetting;
		dateFieldReadyEstimated: ImportTableColumnSetting;
		fieldPriority: ImportTableColumnSetting;
		collectBoundary: ImportTableColumnSetting;
		submissionNotes: ImportTableColumnSetting;

		// Deal
		density: ImportTableColumnSetting;
		depth: ImportTableColumnSetting;
		labName: ImportTableColumnSetting;
		testPackage: ImportTableColumnSetting;
		frequencyOfMicros: ImportTableColumnSetting;
		deal: ImportTableColumnSetting;

		// Lab Info
		labInstructions: ImportTableColumnSetting;
		microsList: ImportTableColumnSetting;
		lastCrop: ImportTableColumnSetting;
		nextCrop: ImportTableColumnSetting;
		yieldGoal: ImportTableColumnSetting;

		// Contacts
		branch: ImportTableColumnSetting;
		salesperson: ImportTableColumnSetting;
		salespersonMobilePhone: ImportTableColumnSetting;
		growerPhoneNumber: ImportTableColumnSetting;
		submitterName: ImportTableColumnSetting;
		submitterEmail: ImportTableColumnSetting;

		// Sync Codes
		eventId: ImportTableColumnSetting;
		fieldId: ImportTableColumnSetting;
		labSubmissionCode: ImportTableColumnSetting;
		externalId: ImportTableColumnSetting;
		externalFarmId: ImportTableColumnSetting;
		externalLabId: ImportTableColumnSetting;

		isTestJob: ImportTableColumnSetting;
	};
}

export type ImportTableColumn = keyof CompanySettings["importTableColumns"];

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// Maintain the same order in all places for consistency.
// ==========================================================================================
export const DefaultImportTableColumns: Record<ImportTableColumn, ImportTableColumnSetting> = {
	// Basic Information
	grower: { visible: true, required: true },
	farm: { visible: true, required: false },
	field: { visible: true, required: true },

	// Attachments
	boundaryGeoJSON: { visible: true, required: true },
	pointsGeoJSON: { visible: true, required: false },
	zonesGeoJSON: { visible: false, required: false },
	fieldNotes: { visible: false, required: false },

	// Order
	isReadyToSample: { visible: true, required: false },
	dateFieldReadyEstimated: { visible: false, required: false },
	fieldPriority: { visible: true, required: false },
	collectBoundary: { visible: true, required: false },
	submissionNotes: { visible: true, required: false },

	// Deal
	density: { visible: true, required: true },
	depth: { visible: true, required: true },
	labName: { visible: false, required: false },
	testPackage: { visible: true, required: false },
	frequencyOfMicros: { visible: true, required: false },
	deal: { visible: true, required: true },

	// Lab Info
	labInstructions: { visible: false, required: false },
	microsList: { visible: true, required: false },
	lastCrop: { visible: false, required: false },
	nextCrop: { visible: false, required: false },
	yieldGoal: { visible: false, required: false },

	// Contacts
	branch: { visible: false, required: false },
	salesperson: { visible: true, required: false },
	salespersonMobilePhone: { visible: true, required: false },
	growerPhoneNumber: { visible: true, required: false },
	submitterName: { visible: false, required: false },
	submitterEmail: { visible: false, required: false },

	// Sync Codes
	eventId: { visible: false, required: false },
	labSubmissionCode: { visible: true, required: false },
	fieldId: { visible: true, required: false },
	externalId: { visible: false, required: false },
	externalFarmId: { visible: false, required: false },
	externalLabId: { visible: false, required: false },

	// Misc
	isTestJob: { visible: false, required: false },
};

//#endregion

//#region File Groups

export const GeoFileTypes = ["Boundary", "Zones", "Centroids", "Cores", "Unknown"] as const;

export type GeoFileType = (typeof GeoFileTypes)[number];

export type Accessor<T, V extends BaseDataModel> = keyof V | ((originalRow: V) => T);

export type RogoFeatures = Feature<Polygon> | Feature<MultiPolygon> | Feature<Point>;

export const FilterTypesCSV = ["Boundary", "Zones", "Points", "Exclude File"] as const;
export type FilterTypeCSV = (typeof FilterTypesCSV)[number];

export const FilterTypes = ["Boundary", "Zones", "Centroids", "Cores", "Exclude"] as const;
export type FilterType = (typeof FilterTypes)[number];

export const ConditionTypes = ["If", "Always"] as const;
export type ConditionType = (typeof ConditionTypes)[number];

export const MatchTypes = ["Contains", "Doesn't Contain", "Exists"] as const;
export type MatchType = (typeof MatchTypes)[number];

type PointsTypes = "Cores" | "Centroids";
type PolygonTypes = "Boundary" | "Zones";
type OptionTypes = PointsTypes | PolygonTypes;

interface FileOptionType {
	type: OptionTypes;
	label: string;
}

export const FileOptionTypes: Record<OptionTypes, FileOptionType> = {
	Cores: { type: "Cores", label: "Cores" },
	Centroids: { type: "Centroids", label: "Centroids" },
	Boundary: { type: "Boundary", label: "Boundaries" },
	Zones: { type: "Zones", label: "Sample Zones" },
};

export interface FileOptionsGroup {
	type: FileOptionType;
	files: File[];
}

export interface GeoJSONOption {
	name: string; // Display name for dropdown (e.g., "Field Boundary", "Centroids 1")
	geoJSON: GeoJSON.FeatureCollection;
}

export interface GeoJSONOptionsGroup {
	type: FileOptionType;
	options: GeoJSONOption[];
}

export interface GeoMetadata {
	groupKey: string;
	dbfKeys: string[];
	dbfValues: string[];
	geoType: GeoFileType;
	path: string;
	geoJSON: GeoJSON.FeatureCollection;
	shapeCounts: Record<string, number>;
}

export type KeyedShapefile = File & GeoMetadata;

export type KeyedShapefileMap = Map<string, KeyedShapefile[]>;

export interface GroupedGeoJSON {
	groupKey: string;
	geoJSON: FeatureCollection;
	propertyNames: string[];
	geometryTypes: GeoJsonGeometryTypes[];
	originalFile?: File;
}

export type GeoJSONDataMap = Map<string, GroupedGeoJSON[]>;

/**
 * Represents a set of features with identical coordinates that appear across multiple files.
 */
export interface CrossGroupDuplicateSet {
	geometryType: "Polygon" | "Point";
	affectedFilePaths: string[]; // User-friendly file paths
	occurrenceCount: number; // How many files contain this coordinate
}

/**
 * Results from duplicate detection across files.
 * Tracks both cross-file duplicates and within-file duplicates.
 */
export interface CrossGroupDuplicateData {
	hasCrossGroupDuplicates: boolean;
	polygonCrossGroupCount: number; // Unique polygon coords in 2+ files
	pointCrossGroupCount: number; // Unique point coords in 2+ files
	totalAffectedFeatures: number; // Total features involved in cross-file duplicates
	duplicateSets: CrossGroupDuplicateSet[]; // Detailed breakdown (limited to first 10)
	allAffectedFilePaths: string[]; // All files containing ANY duplicates (cross-file or within-file)
}

export type GroupedGeoFiles = Record<string, Record<GeoFileType, File[]>>;

export interface CategorizedGeoJSON {
	Boundary: FeatureCollection[];
	Zones: FeatureCollection[];
	Centroids: FeatureCollection[];
	Cores: FeatureCollection[];
	Unknown: FeatureCollection[];
}

export type GroupedGeoJSONFiles = Record<string, CategorizedGeoJSON>;

export type RogoFile = File & {
	shapeCounts?: Record<string, number>;
};

export type CategorizedFiles = Record<GeoFileType, RogoFile[]>;

//#endregion

//#region Fuzzy Matching

/**
 * Generic interface for ID-based dropdown options.
 * Used for fuzzy matching where we match against 'name' but return 'id'.
 */
export interface IdDropdownOption {
	id: string;
	name: string;
}

//#endregion

//#region Import

export interface CSVImportRows {
	[name: string]: unknown;
	growerName: string;
	growerEmail?: string;
	growerCell?: string;
	fieldName: string;
	fieldID?: string;
	farmName?: string;
	depth?: string;
	labName?: string;
	locationFromForm: string;
	gridSize?: string;
	testPackage?: string;
	ready?: string;
	fieldPriority?: string;
	syncCode: string;
	submissionNotes?: string;
	labAccountNumber?: string;
	freqOfMicros?: string;
	submitterName?: string;
	submitterEmail?: string;
	pointsAttached?: string;
	jobFlags: "Bulk Upload" | "Bulk Upload - Completed";
	boundaryChange: "Major" | "Minor" | undefined;
	specialLabInstructions: string;
	lastCrop: string;
	nextCrop: string;
	specsFromForm: string;
	regularLabSubmissionCode: string;
}

export class ImportOption {
	id: number;
	label: string;
	shortLabel: string;
	superUserLabel: string;
	superUserShortLabel: string;
	private iconComponent: SvgIconComponent;
	private _icon?: React.ReactNode;

	constructor(
		id: number,
		icon: SvgIconComponent,
		label: string,
		shortLabel?: string,
		superUserLabel?: string,
		superUserShortLabel?: string,
	) {
		this.id = id;
		this.label = label;
		this.shortLabel = shortLabel ?? label;
		this.superUserLabel = superUserLabel ?? label;
		this.superUserShortLabel = superUserShortLabel ?? this.shortLabel;
		this.iconComponent = icon;
	}

	get icon(): React.ReactNode {
		if (!this._icon) {
			this._icon = React.createElement(this.iconComponent, { className: "icon-in-button" });
		}
		return this._icon;
	}
}

export const ImportOptionChoices = {
	SingleUpload: new ImportOption(1, PlusOne, "Single Upload", "Single", "Single", ""),
	BulkUpload: new ImportOption(2, UploadFile, "Bulk Upload", "Bulk", "Bulk", ""),
	BulkUpload2: new ImportOption(3, UploadFile, "Bulk Upload 2.0", "Bulk 2.0", "Bulk 2.0", "2.0"),
	Agvance: new ImportOption(4, Upload, "AgVance", "AgVance", "AgVance", "Agv"),
	Proagrica: new ImportOption(5, Upload, "Proagrica / AGX", "Proagrica", "Proagrica", "Agx"),
	Agrian: new ImportOption(6, Upload, "Import", "Import", "Agn", "Agn"),
};

//#endregion
